import axios from "axios";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import AuthContext from "../../contexts/auth/AuthContext.jsx";
import Mobile2BEmbedApiService from "../../services/apiServices/Mobile2BEmbedApiService.jsx";
import Loading from "../ui-elements/Loading.jsx";
import UIContext from "@/contexts/ui/UIContext.js";

const MaintenanceTask = () => {
    const {currentTenant, currentContainer} = useContext(AuthContext);
    const [iframeSrc, setIframeSrc] = useState(null);
    const [iframeRefreshKey, setIframeRefreshKey] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const abortControllerRef = useRef(null);
    const { setInfo } = useContext(UIContext);

    const loadMobile2B = useCallback(async () => {
        abortControllerRef.current?.abort();

        const abortController = new AbortController();
        abortControllerRef.current = abortController;

        setIsLoading(true);
        setError(null);

        try {
            if (!currentTenant?.id || !currentContainer?.id) {
                throw new Error("No FarmerApp context selected.");
            }

            const response = await Mobile2BEmbedApiService.getEmbedSessionAsync(currentTenant.id, currentContainer.id, abortController.signal);
            const iframeUrl = response.data?.data?.iframeUrl;

            if (!iframeUrl) {
                throw new Error("No Mobile2B iframe URL returned.");
            }

            if (abortController.signal.aborted) {
                return;
            }

            setIframeSrc(iframeUrl);
            setIframeRefreshKey((currentKey) => currentKey + 1);
        } catch (err) {
            if (axios.isCancel(err) || err?.code === "ERR_CANCELED" || abortController.signal.aborted) {
                return;
            }

            console.error("Could not prepare Mobile2B iframe:", err);
            setError("Maintenance Task could not be opened with the current FarmerApp session.");
        } finally {
            if (abortControllerRef.current === abortController) {
                abortControllerRef.current = null;
            }

            if (!abortController.signal.aborted) {
                setIsLoading(false);
            }
        }
    }, [currentTenant?.id, currentContainer?.id]);

    useEffect(() => {
        setInfo('Maintenance Task');
        loadMobile2B();

        return () => {
            abortControllerRef.current?.abort();
        };
    }, [loadMobile2B]);

    if (isLoading && !iframeSrc) {
        return <Loading fullScreen />;
    }

    if (error && !iframeSrc) {
        return (
            <section className="maintenance-task maintenance-task--status">
                <div className="maintenance-task-status">
                    <h2 className="maintenance-task-status__title">Maintenance Task</h2>
                    <p className="maintenance-task-status__message">{error}</p>
                    <button
                        type="button"
                        onClick={loadMobile2B}
                        className="sweden-ui-input-button maintenance-task-status__retry"
                        data-type="inline"
                    >
                        Retry
                    </button>
                </div>
            </section>
        );
    }

    return (
        <section className="maintenance-task">
            <iframe
                key={iframeRefreshKey}
                src={iframeSrc}
                title="Maintenance Task"
                sandbox="allow-downloads allow-forms allow-modals allow-orientation-lock allow-pointer-lock allow-popups allow-popups-to-escape-sandbox allow-presentation allow-same-origin allow-scripts allow-storage-access-by-user-activation allow-top-navigation allow-top-navigation-by-user-activation allow-top-navigation-to-custom-protocols"
                className="maintenance-task__frame"
            />
        </section>
    );
};

export default MaintenanceTask;
